'use client'

import { useMapboxContext } from '@component/mapbox/map/context'
import { useEffect } from 'react'
import bbox from '@turf/bbox'
import { assign, isArray } from 'lodash-es'
import { boundaryToLngLat } from '@component/mapbox/utils'

export default function MapboxPolyline({
  data,
  autofitbounds = false,
  color = '#FF8500',
  width = 4,
  opacity = 1,
  dasharray,
  id
}) {

  const { id: mapId, map, mapLoaded } = useMapboxContext()
  useEffect(() => {

    if (!mapLoaded) return

    const combineId = id || mapId
    if (mapLoaded) {
      let paths = data
      if (isArray(data)) {
        paths = {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              properties: {},
              geometry: {
                type: 'LineString',
                coordinates: boundaryToLngLat(data)
              }
            }
          ]
        }
      }
      addPolyline({ autofitbounds, color, width, opacity, dasharray })(map, combineId, paths)
    }

    return () => {
      removePolyline(map, combineId)
    }
  }, [mapLoaded, map, mapId, id, autofitbounds, color, width, opacity, dasharray, data])
}

function addPolyline({
  autofitbounds = false,
  color,
  width = 4,
  opacity = 1,
  dasharray
}) {

  function addLayer(map, data, id) {
    const polylineData = {
      'id': id,
      'type': 'line',
      'source': {
        'type': 'geojson',
        'data': data
      },
      'layout': {
        'line-join': 'round',
        'line-cap': 'round'
      },
      'paint': assign({
        'line-color': color,
        'line-width': width,
        'line-opacity': opacity,
      }, dasharray ? { 'line-dasharray': dasharray } : {})
    }
    map.addLayer(polylineData)
    fitBounds(map, data, autofitbounds)
  }
  function setLayer(map, data, id) {
    map.getSource(id).setData(data)
    fitBounds(map, data, autofitbounds)
  }
  function fitBounds(map, data, options) {
    if (!autofitbounds) return
    let bounds = bbox(data)
    map.fitBounds(bounds, assign({
      padding: 30,
    }, options))
  }

  return (map, id, data) => {
    const polylineId = `${id}-polyline`
    if (map.getLayer(polylineId)) {
      setLayer(map, data, polylineId)
    } else {
      addLayer(map, data, polylineId)
    }
  }
}

function removePolyline(map, id) {
  const polylineId = `${id}-polyline`
  if (map.getLayer(polylineId)) {
    map.removeLayer(polylineId)
  }
  if (map.getSource(polylineId)) {
    map.removeSource(polylineId)
  }
}